import { lazy, Suspense } from 'react';

import { Spinner } from '@/common/components';
import { AppControllerType, AppRoutes } from '@/common/interfaces';

import { DynamicBaseBreadcrumb, DynamicDomainsApiBreadcrumb, DynamicDomainsBreadcrumb, DynamicSitesBreadcrumb } from '@/modules/AppBreadcrumb';


const SearchConsoleAccountsListPage = lazy(
  () => import('../../../pages/DomainsApi/SearchConsole/SearchConsoleAccountsList.page')
);

const SearchConsoleAccountsPage = lazy(() => import('../../../pages/DomainsApi/SearchConsole/SearchConsoleAccounts.page'));

const SearchConsoleAccountsFormSteps = lazy(
  () => import('../../../pages/DomainsApi/SearchConsole/SearchConsoleAccountsForm.page')
);

const SearchConsoleAccountsCreateFirstStep = lazy(
  () => import('../../../pages/DomainsApi/SearchConsole/SearchConsoleAccountsCreate/SearchConsoleAccountsCreateFirstStep.page')
);
const SearchConsoleAccountsCreateSecondStep = lazy(
  () => import('../../../pages/DomainsApi/SearchConsole/SearchConsoleAccountsCreate/SearchConsoleAccountsCreateSecondStep.page')
);
const SearchConsoleAccountsCreateThirdStep = lazy(
  () => import('../../../pages/DomainsApi/SearchConsole/SearchConsoleAccountsCreate/SearchConsoleAccountsCreateThirdStep.page')
);

const SearchConsoleAccountsEditFirstStep = lazy(
  () => import('../../../pages/DomainsApi/SearchConsole/SearchConsoleAccountsEdit/SearchConsoleAccountsEditFirstStep.page')
);
const SearchConsoleAccountsEditSecondStep = lazy(
  () => import('../../../pages/DomainsApi/SearchConsole/SearchConsoleAccountsEdit/SearchConsoleAccountsEditSecondStep.page')
);

const SearchConsoleOverviewPage = lazy(() => import('../../../pages/Domains/SearchConsole/SearchConsoleOverview.page'));

const SearchConsoleSitemapPage = lazy(() => import('../../../pages/Domains/SearchConsole/Sitemap/SearchConsoleSitemap.page'));
const SearchConsoleSitemapDossierPage = lazy(
  () => import('../../../pages/Domains/SearchConsole/Sitemap/SearchConsoleSitemapDossier.page')
);
const SearchConsoleSearchAnalyticsPage = lazy(
  () => import('../../../pages/Domains/SearchConsole/SearchAnalytics/SearchConsoleAnalytics.page')
);
const SearchConsoleSearchAnalyticsDossierPage = lazy(
  () => import('../../../pages/Domains/SearchConsole/SearchAnalytics/SearchConsoleAnalyticsDossier.page')
);
const SearchConsoleSearchUrlInspectionPage = lazy(
  () => import('../../../pages/Domains/SearchConsole/UrlInspection/SearchConsoleUrlInspection.page')
);
const SearchConsoleSearchUrlInspectionDossierPage = lazy(
  () => import('../../../pages/Domains/SearchConsole/UrlInspection/SearchConsoleUrlInspectionDossier.page')
);

const { SearchConsoleAccountsBreadcrumb, DynamicSearchConsoleBreadcrumb } = DynamicDomainsApiBreadcrumb;
const {
  DynamicSearchConsoleAccountsByIdBreadcrumb,
  DynamicSearchConsoleAccountsListBreadcrumb,
  DynamicSearchConsoleCredsBreadcrumb,
  DynamicSearchConsoleOAuth2Breadcrumb
} = SearchConsoleAccountsBreadcrumb;

export const DomainsApiSearchConsoleController: AppControllerType = {
  common: [
    {
      path: AppRoutes.DomainsApiSearchConsole + '/*',
      breadcrumb: DynamicSearchConsoleBreadcrumb
    },
    {
      path: AppRoutes.DomainsApiSearchConsoleAccounts + '/*',
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SearchConsoleAccountsListPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSearchConsoleAccountsListBreadcrumb
    },
    // MARK: - Search Console
    {
      path: AppRoutes.SiteSearchConsole,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SearchConsoleOverviewPage/>
        </Suspense>
      ),
      // @ts-ignore
      children: [
        // MARK: - SC Sitemap
        {
          path: AppRoutes.SiteSearchConsoleSitemap + '/*',
          element: (
            <Suspense fallback={ <Spinner/> }>
              <SearchConsoleSitemapPage/>
            </Suspense>
          ),
          breadcrumb: DynamicSitesBreadcrumb.DynamicSearchConsoleSitemapBreadcrumb
        },
        // MARK: - SC Search Analytics
        {
          path: AppRoutes.SiteSearchConsoleSearchAnalytics + '/*',
          element: (
            <Suspense fallback={ <Spinner/> }>
              <SearchConsoleSearchAnalyticsPage/>
            </Suspense>
          ),
          breadcrumb: DynamicSitesBreadcrumb.DynamicSearchConsoleAnalyticsBreadcrumb
        },
        // MARK: - SC Url Inspection
        {
          path: AppRoutes.SiteSearchConsoleSearchUrlInspection + '/*',
          element: (
            <Suspense fallback={ <Spinner/> }>
              <SearchConsoleSearchUrlInspectionPage/>
            </Suspense>
          ),
          breadcrumb: DynamicSitesBreadcrumb.DynamicSearchConsoleUrlInspectionBreadcrumb
        }
      ],
      breadcrumb: DynamicSitesBreadcrumb.DynamicSearchConsoleBreadcrumb
    }
  ],
  drawer: [
    // MARK: - SC Accounts Create
    {
      path: AppRoutes.DomainsApiSearchConsoleAccountsCreate,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SearchConsoleAccountsFormSteps/>
        </Suspense>
      ),
      breadcrumb: DynamicBaseBreadcrumb.DynamicBaseCreateBreadcrumb,
      props: {
        commonDrawerWidth: 460
      },
      // @ts-ignore
      children: [
        {
          path: AppRoutes.DomainsApiSearchConsoleAccountsCreate,
          element: (
            <Suspense fallback={ <Spinner/> }>
              <SearchConsoleAccountsCreateFirstStep/>
            </Suspense>
          ),
          breadcrumb: DynamicBaseBreadcrumb.DynamicBaseCreateBreadcrumb,
          props: {
            commonDrawerWidth: 460
          }
        },
        {
          path: AppRoutes.DomainsApiSearchConsoleAccountsCreateCreds,
          element: (
            <Suspense fallback={ <Spinner/> }>
              <SearchConsoleAccountsCreateSecondStep/>
            </Suspense>
          ),
          breadcrumb: DynamicSearchConsoleCredsBreadcrumb,
          props: {
            commonDrawerWidth: 460
          }
        },
        {
          path: AppRoutes.DomainsApiSearchConsoleAccountsCreateOauth2,
          element: (
            <Suspense fallback={ <Spinner/> }>
              <SearchConsoleAccountsCreateThirdStep/>
            </Suspense>
          ),
          breadcrumb: DynamicSearchConsoleOAuth2Breadcrumb,
          props: {
            commonDrawerWidth: 460
          }
        }
      ]
    },
    // MARK: - SC Accounts Edit
    {
      path: AppRoutes.DomainsApiSearchConsoleAccountsEdit,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SearchConsoleAccountsFormSteps/>
        </Suspense>
      ),
      breadcrumb: DynamicBaseBreadcrumb.DynamicBaseEditBreadcrumb,
      props: {
        commonDrawerWidth: 460
      },
      // @ts-ignore
      children: [
        {
          path: AppRoutes.DomainsApiSearchConsoleAccountsEdit,
          element: (
            <Suspense fallback={ <Spinner/> }>
              <SearchConsoleAccountsEditFirstStep/>
            </Suspense>
          ),
          breadcrumb: DynamicBaseBreadcrumb.DynamicBaseEditBreadcrumb,
          props: {
            commonDrawerWidth: 460
          }
        },
        {
          path: AppRoutes.DomainsApiSearchConsoleAccountsEditCreds,
          element: (
            <Suspense fallback={ <Spinner/> }>
              <SearchConsoleAccountsEditSecondStep/>
            </Suspense>
          ),
          breadcrumb: DynamicSearchConsoleCredsBreadcrumb,
          props: {
            commonDrawerWidth: 460
          }
        }
      ]
    },
    // MARK: - SC Accounts
    {
      path: AppRoutes.DomainsApiSearchConsoleAccountsById,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SearchConsoleAccountsPage/>
        </Suspense>
      ),
      breadcrumb: DynamicSearchConsoleAccountsByIdBreadcrumb,
      props: {
        commonDrawerWidth: 460
      }
    },
    // MARK: - SC Sitemap by ID
    {
      path: AppRoutes.SiteSearchConsoleSitemapById,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SearchConsoleSitemapDossierPage/>
        </Suspense>
      ),
      breadcrumb: DynamicDomainsBreadcrumb.DynamicDomainsByIdBreadcrumb,
      props: {
        commonDrawerWidth: 460
      }
    },
    // MARK: - SC Analytics by ID
    {
      path: AppRoutes.SiteSearchConsoleSearchAnalyticsById,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SearchConsoleSearchAnalyticsDossierPage/>
        </Suspense>
      ),
      breadcrumb: DynamicDomainsBreadcrumb.DynamicDomainsByIdBreadcrumb,
      props: {
        // commonDrawerWidth: 'calc(50vw - 220px)'
        commonDrawerWidth: 500
      }
    },
    // MARK: - SC Url Inspection by ID
    {
      path: AppRoutes.SiteSearchConsoleSearchUrlInspectionById,
      element: (
        <Suspense fallback={ <Spinner/> }>
          <SearchConsoleSearchUrlInspectionDossierPage/>
        </Suspense>
      ),
      breadcrumb: DynamicDomainsBreadcrumb.DynamicDomainsByIdBreadcrumb,
      props: {
        commonDrawerWidth: 520
      }
    }
  ]
};
